import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'create-webhook' as const;

// Define the available trigger types as an enum
const triggerTypeEnum = z.enum([
  'form_submission',
  'site_publish',
  'page_created',
  'page_metadata_updated',
  'page_deleted',
  'ecomm_new_order',
  'ecomm_order_changed',
  'ecomm_inventory_changed',
  'user_account_added',
  'user_account_updated',
  'user_account_deleted',
]);

// Define the input schema based on Webflow API docs
const argumentZod = z.object({
  triggerType: triggerTypeEnum
    .optional()
    .describe(
      'The type of event that will trigger the webhook. Each trigger type corresponds to a specific event in Webflow.'
    ),
  url: z
    .string()
    .url()
    .optional()
    .describe(
      'The URL that will receive the webhook payload when the trigger event occurs'
    ),
  filter: z
    .object({
      name: z.string().optional(),
    })
    .optional()
    .describe(
      'Optional filter to specify which events should trigger the webhook (currently only supported for form_submission)'
    ),
});

export const createWebhookManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create webhook',
  family: 'webhooks',
  llmTool: {
    name: toolName,
    description:
      'Creates a new webhook for the current site. Webhooks allow you to receive notifications when specific events occur on your site, such as form submissions, site publishes, or CMS item changes.',
    inputSchema: argumentZod,
  },
  type: 'server',
  permission: [{resource: 'SiteWebhook', action: 'manage'}],
  confirmBeforeExecuting: true, // Require confirmation for webhook creation
});

export type CreateWebhookArgumentType = z.infer<typeof argumentZod>;
